import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, Compass, FileSignature, FileText, Image as ImageIcon, Layers3, Landmark, ListChecks, Scale, ScanSearch, ShieldCheck, Sparkles, WalletCards, X } from 'lucide-react';
import { cadreBZSimplePiliers } from '../data/cadreBZMapping';

export type ODSKnowledgeTileId =
  | 'bots'
  | 'principes'
  | 'structure'
  | 'trois-choix'
  | 'qualification-contexte'
  | 'inclusions-limites'
  | 'piliers-facteurs'
  | 'tarification-investissement'
  | 'personnalisation-client'
  | 'termes-conditions'
  | 'convention-service';

interface ODSKnowledgeTileModalProps {
  tile: ODSKnowledgeTileId | null;
  isOpen: boolean;
  onClose: () => void;
}

type TileContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  source: string;
  icon: typeof ShieldCheck;
  accent: string;
  sections: Array<{
    title: string;
    body?: string;
    bullets?: string[];
    imageUrl?: string;
    imageAlt?: string;
  }>;
};

const tiles: Record<ODSKnowledgeTileId, TileContent> = {
  bots: {
    eyebrow: 'ODS BZ · Tuile 01',
    title: 'Instructions communes pour les bots',
    subtitle: 'Les règles minimales que tout bot BZ doit suivre avant de structurer, réviser ou générer une ODS.',
    source: 'Inspiré de la tuile « Guide pour création d’ODS » et aligné sur PRIsME comme source principale.',
    icon: ShieldCheck,
    accent: 'from-sky-500 via-cyan-500 to-blue-600',
    sections: [
      {
        title: 'Rôle de cette tuile',
        body: 'Définir les règles communes pour que les bots restent cohérents, prudents et utiles lorsqu’ils travaillent sur une ODS BZ.',
      },
      {
        title: 'Règles non négociables',
        bullets: [
          'Ne jamais inventer un prix, une clause, une inclusion, une condition contractuelle ou un délai.',
          'Distinguer les faits confirmés, les hypothèses, les recommandations et les éléments à valider.',
          'Utiliser le vocabulaire BZ : piliers, accompagnement, trajectoires et responsabilités.',
          'Ne jamais inventer de nouveaux piliers : seuls les 6 piliers officiels de l’Ébauche de cadre client BZ sont permis.',
          'Les piliers BZ doivent absolument apparaître dans chaque ODS : ce n’est pas une section optionnelle.',
          'Définir les piliers BZ en début d’ODS, puis préciser les piliers réellement touchés par chaque option.',
          'Ne jamais créer de piliers secondaires : les limites ou éléments hors portée vont dans le commentaire de l’option.',
          'Ne pas transformer les dimensions transversales en piliers ODS.',
          'Comparer chaque ODS à la Convention de service BZ 2026 V1.1 et signaler tout écart dans l’ODS ainsi que dans le chat.',
          'Offrir trois choix par défaut, sauf demande explicite d’un produit ou d’une portée précise.',
          'Toujours inclure les termes et conditions BZ approuvés; en l’absence d’une demande explicite et autorisée du DDC, appliquer la version standard complète sans modification.',
          'Éviter les promesses de résultat garanti et parler en objectifs visés, livrables et conditions de réussite.',
        ],
      },
      {
        title: 'Posture attendue',
        body: 'Le bot peut préparer, structurer et vérifier une ODS, mais il ne remplace pas la validation humaine. Une ODS client doit être relue avant envoi, surtout si elle touche le prix, la portée technique, la cybersécurité, l’IA ou une exception contractuelle.',
      },
    ],
  },
  principes: {
    eyebrow: 'ODS BZ · Tuile 02',
    title: 'Principes d’une bonne ODS',
    subtitle: 'Une ODS BZ est un document de décision, pas une simple soumission ni une liste de produits.',
    source: 'Reprend l’esprit du Guide pour création d’ODS : comprendre, comparer, choisir, accompagner.',
    icon: Compass,
    accent: 'from-violet-500 via-fuchsia-500 to-indigo-600',
    sections: [
      {
        title: 'Définition pratique',
        body: 'Une bonne ODS aide le client à comprendre sa situation, les options disponibles, les compromis, l’investissement, les limites de portée et la prochaine décision à prendre.',
      },
      {
        title: 'Ce qu’elle doit faire',
        bullets: [
          'Partir du contexte client : faits, irritants, ambitions, risques et contraintes.',
          'Présenter la valeur BZ comme un accompagnement, pas comme une transaction de produits.',
          'Rendre les options comparables et défendables.',
          'Exposer clairement inclusions, exclusions, dépendances, responsabilités et risques résiduels.',
          'Mener à une décision simple et actionnable.',
        ],
      },
      {
        title: 'Ce qu’elle doit éviter',
        body: 'Une ODS ne doit pas promettre de résultat garanti, cacher les limites, surcharger le client de détails techniques ou recommander une option sans justification liée au contexte.',
      },
    ],
  },
  structure: {
    eyebrow: 'ODS BZ · Tuile 03',
    title: 'Structure standard d’une ODS',
    subtitle: 'Le squelette recommandé pour passer du contexte client à une décision claire.',
    source: 'Basé sur la section « Structure ODS » de la tuile Guide pour création d’ODS.',
    icon: FileText,
    accent: 'from-emerald-500 via-teal-500 to-cyan-600',
    sections: [
      {
        title: 'Ordre logique recommandé',
        bullets: [
          'Page couverture, contexte client et enjeux observés.',
          'Piliers BZ concernés : définir les piliers officiels touchés par l’ODS avant les options.',
          'Objectifs visés et résultats recherchés.',
          'Options proposées et comparatif des trajectoires.',
          'Recommandation ou orientation préliminaire si les informations le justifient.',
          'Portée incluse, exclusions, dépendances et responsabilités.',
          'Investissement, séquence d’implantation et conditions spécifiques.',
          'Termes et conditions BZ approuvés, puis acceptation et signatures.',
        ],
      },
      {
        title: 'Principe de rédaction',
        body: 'La structure doit rester décisionnelle. Les sections techniques servent à appuyer la décision, mais ne doivent pas remplacer la lecture d’affaires.',
      },
      {
        title: 'Piliers par option',
        bullets: [
          'Chaque option doit indiquer les piliers BZ réellement touchés par sa portée.',
          'Afficher une seule liste de piliers par option : pas de piliers secondaires, indirects ou décoratifs.',
          'Si un pilier n’est pas couvert ou seulement limité, l’expliquer dans le commentaire de l’option plutôt que dans une deuxième catégorie.',
        ],
      },
      {
        title: 'Mise en œuvre',
        body: 'Avant le choix d’une option, présenter une séquence commune de prise en charge, stabilisation et planification. Les initiatives propres à une option viennent après la sélection.',
      },
    ],
  },
  'trois-choix': {
    eyebrow: 'ODS BZ · Tuile 04',
    title: 'Règle des trois choix',
    subtitle: 'Trois trajectoires réelles, avec des noms libres selon le client, le contexte et la préférence de l’utilisateur.',
    source: 'Aligné sur le Guide pour création d’ODS : les options doivent être concrètes, distinctes et défendables.',
    icon: Layers3,
    accent: 'from-amber-500 via-orange-500 to-rose-600',
    sections: [
      {
        title: 'Pourquoi trois choix?',
        body: 'Les trois choix permettent au client de comparer les compromis entre budget, profondeur d’accompagnement, cadence, portée, risques résiduels et ambition.',
      },
      {
        title: 'Les trois trajectoires',
        bullets: [
          'Le bot ne doit pas imposer les noms des options. Les noms doivent être choisis par l’utilisateur ou adaptés au contexte client.',
          'Les options doivent plutôt représenter trois niveaux de portée, d’accompagnement, d’investissement ou d’ambition clairement distincts.',
          'Exemples possibles seulement : Stabilisation / Optimisation / Accélération, Base / Recommandée / Avancée, Court terme / Structuré / Stratégique.',
        ],
      },
      {
        title: 'Exception acceptée',
        body: 'Si le client demande explicitement un produit, une licence, un équipement ou une intervention précise, il ne faut pas inventer trois fausses options. L’ODS peut être ciblée, avec variantes ou ajouts seulement s’ils aident réellement la décision.',
      },
    ],
  },
  'qualification-contexte': {
    eyebrow: 'ODS BZ · Tuile 05',
    title: 'Qualification du contexte client',
    subtitle: 'Des repères souples pour comprendre pourquoi l’ODS existe avant de parler de portée, d’options ou de prix.',
    source: 'Cadre de rédaction ODS BZ : partir du contexte, distinguer faits et hypothèses, puis seulement bâtir les options.',
    icon: ScanSearch,
    accent: 'from-blue-500 via-sky-500 to-cyan-600',
    sections: [
      {
        title: 'Intention',
        body: 'Cette tuile ne doit pas devenir un questionnaire rigide. Elle aide le DDC ou le bot à comprendre le déclencheur, la situation actuelle, les irritants, les attentes et les contraintes avant de rédiger.',
      },
      {
        title: 'Questions repères',
        bullets: [
          'Pourquoi cette ODS maintenant : renouvellement, correction, opportunité, insatisfaction, risque ou nouvelle initiative?',
          'Qu’est-ce qui fonctionne bien aujourd’hui et doit être conservé?',
          'Qu’est-ce qui crée de la lourdeur, du risque, de la confusion ou un déséquilibre?',
          'Qui décide, qui influence et qui devra changer ses habitudes si l’option est retenue?',
          'Quels faits sont confirmés et quels éléments restent à valider avant l’envoi client?',
        ],
      },
      {
        title: 'Posture attendue',
        body: 'Le contexte doit cadrer la décision sans exposer inutilement les irritants politiques internes du client. Si les informations sont incomplètes, utiliser des hypothèses ou une orientation préliminaire plutôt qu’une recommandation ferme.',
      },
    ],
  },
  'inclusions-limites': {
    eyebrow: 'ODS BZ · Tuile 06',
    title: 'Inclusions, exclusions et limites',
    subtitle: 'Comment cadrer la portée sans rendre l’offre lourde, négative ou trop juridique.',
    source: 'Cadre ODS BZ : clarifier ce que BZ fait, ce qui est additionnel, ce qui est hors portée et ce qui dépend du client ou de tiers.',
    icon: ListChecks,
    accent: 'from-emerald-500 via-green-500 to-teal-600',
    sections: [
      {
        title: 'Intention',
        body: 'Les inclusions et exclusions ne sont pas là pour fermer la porte au client. Elles servent à aligner les attentes, éviter les zones grises et protéger la qualité de l’accompagnement.',
      },
      {
        title: 'À préciser lorsque pertinent',
        bullets: [
          'Services inclus, volumes, fréquences, livrables, canaux, rôles et responsabilités.',
          'Options additionnelles comme onboarding personnalisé, visites additionnelles, formation, atelier ou audit complémentaire.',
          'Exclusions : projets majeurs, achats non inclus, frais tiers, développements spécifiques ou changements de portée.',
          'Limites : accès disponibles, adoption des utilisateurs, délais fournisseurs, décisions internes et qualité des informations fournies.',
          'Obligation de moyens : parler en activités, livrables, dépendances et objectifs visés plutôt qu’en résultats garantis.',
        ],
      },
      {
        title: 'Formulation utile',
        body: 'Une bonne ODS indique clairement ce qui est inclus, ce qui est disponible sur demande, ce qui sera facturé ou validé séparément et ce qui doit être confirmé avant la version finale.',
      },
    ],
  },
  'piliers-facteurs': {
    eyebrow: 'ODS BZ · Tuile 07',
    title: 'Piliers BZ',
    subtitle: 'Relier une ODS aux six piliers officiels de l’Ébauche de cadre client BZ, sans reprendre les dimensions transversales.',
    source: 'Source de vérité : tuile « Ébauche de cadre client BZ » dans Gouvernance et Conformité / Ventes & Marketing. Cette tuile ODS utilise seulement les piliers, pas les dimensions transversales.',
    icon: Landmark,
    accent: 'from-indigo-500 via-blue-500 to-sky-600',
    sections: [
      {
        title: 'Intention',
        body: 'Les piliers servent à expliquer la valeur de l’accompagnement BZ. Ils ne doivent pas devenir une décoration, une table des matières obligatoire ou être remplacés par des piliers inventés pour le contexte client.',
      },
      {
        title: 'Les 6 piliers officiels du cadre client BZ',
        bullets: cadreBZSimplePiliers.map((pilier, index) => `${index + 1}. ${pilier.nom} — ${pilier.description}`),
      },
      {
        title: 'Exclusion explicite — dimensions transversales',
        body: 'Les dimensions transversales du cadre client BZ ne doivent pas apparaître dans une ODS — ni comme piliers, ni comme sous-piliers, ni dans la définition initiale, les options, les tableaux comparatifs ou les listes de couverture.',
        bullets: [
          'Ne pas afficher « Gestion des actifs & parties prenantes » dans l’ODS.',
          'Ne pas afficher « Gestion de projet & amélioration continue » dans l’ODS.',
          'Ces dimensions restent internes au cadre BZ et ne doivent pas être ajoutées au contenu client, même si elles soutiennent l’exécution du mandat.',
        ],
      },
      {
        title: 'Utilisation dans une ODS',
        bullets: [
          'Les piliers BZ doivent apparaître dans chaque ODS et être définis en début de document.',
          'Nommer seulement les piliers réellement touchés par l’ODS et par chacune des options.',
          'Relier chaque pilier mentionné à une portée, une activité, une responsabilité ou une décision concrète.',
          'Ne pas créer de liste de piliers secondaires : une option a une seule liste de piliers touchés.',
          'Ne pas inventer de pilier additionnel pour accommoder un contexte client.',
        ],
      },
    ],
  },
  'tarification-investissement': {
    eyebrow: 'ODS BZ · Tuile 08',
    title: 'Tarification et investissement',
    subtitle: 'Présenter le prix comme la conséquence d’un niveau de service, d’une portée et d’un modèle de collaboration.',
    source: 'Cadre ODS BZ : distinguer mensuel, frais uniques, options additionnelles, variables et hypothèses sans transformer l’ODS en fichier Excel.',
    icon: WalletCards,
    accent: 'from-amber-500 via-yellow-500 to-orange-600',
    sections: [
      {
        title: 'Intention',
        body: 'Le client doit comprendre ce qu’il choisit, pas seulement ce qu’il paie. L’investissement doit être clair, comparable et relié à la portée.',
      },
      {
        title: 'À distinguer',
        bullets: [
          'Mensualité récurrente : service, licences, sauvegardes, cybersécurité, IA ou accompagnement continu.',
          'Frais uniques : démarrage, migration, configuration, atelier, onboarding ou transition.',
          'Options additionnelles : formation, onboarding personnalisé, visites additionnelles, audit ou accompagnement IA.',
          'Coûts variables : quantités, usage, fournisseurs, SKU, stockage, équipements, déplacements ou nombre d’utilisateurs.',
        ],
      },
      {
        title: 'Discipline de prix',
        body: 'Ne jamais inventer un prix. Garder les marges et calculs internes hors du document client. Si une quantité est inconnue, présenter le prix unitaire et indiquer que le total dépendra du volume retenu.',
      },
    ],
  },
  'personnalisation-client': {
    eyebrow: 'ODS BZ · Tuile 09',
    title: 'Personnalisation client de chaque ODS',
    subtitle: 'Chaque ODS doit sentir qu’elle a été préparée pour ce client précis — pas générée à partir d’un modèle générique.',
    source: 'Cadre ODS BZ : renforcer la pertinence commerciale par une page couverture personnalisée, les logos client + BZ et un contexte réellement adapté.',
    icon: ImageIcon,
    accent: 'from-rose-500 via-pink-500 to-orange-500',
    sections: [
      {
        title: 'Principe directeur',
        body: 'Une ODS personnalisée augmente la crédibilité de BZ : le client doit reconnaître son entreprise, son contexte, ses priorités et son niveau de maturité dès les premières pages.',
      },
      {
        title: 'Page couverture attendue',
        bullets: [
          'Afficher le logo du client lorsque son site web public permet de l’identifier clairement.',
          'Afficher aussi le logo de BZ pour positionner l’ODS comme un document co-brandé et professionnel.',
          'Inscrire le nom de l’entreprise cliente, le titre de l’ODS, la date, le DDC responsable et la version lorsque pertinent.',
          'Conserver un style sobre, moderne et décisionnel : les logos doivent soutenir la confiance, pas alourdir la page.',
        ],
      },
      {
        title: 'Logo BZ à utiliser',
        body: 'Pour la marque BZ, utiliser en priorité le logo officiel disponible sur le site web de BZ. Si cette version ne peut pas être récupérée correctement, utiliser le logo de référence fourni dans PRIsME ci-dessous; ne pas recréer, modifier ni remplacer le logo par une version non officielle.',
        bullets: [
          'Source prioritaire : le site web officiel de BZ.',
          'Solution de repli approuvée : /brand/bz-logo-experts-ti-cybersecurite.png.',
          'Conserver les proportions, les couleurs et une zone de dégagement suffisante autour du logo.',
        ],
        imageUrl: '/brand/bz-logo-experts-ti-cybersecurite.png',
        imageAlt: 'Logo officiel BZ — Experts en TI et Stratégies en Cybersécurité',
      },
      {
        title: 'Recherche du logo client par le bot',
        bullets: [
          'Utiliser d’abord le site web officiel du client fourni ou confirmé dans le contexte.',
          'Repérer le logo dans l’en-tête, le pied de page, les métadonnées Open Graph, le favicon haute résolution ou les fichiers publics de marque.',
          'Ne pas inventer ni recréer un logo. Si le logo est ambigu, basse qualité ou introuvable, demander explicitement au DDC de fournir le logo officiel du client avant de finaliser la page couverture.',
          'Éviter les sources non officielles sauf validation explicite : annuaires, réseaux sociaux, banques d’images ou vieux PDF peuvent être désuets.',
        ],
      },
      {
        title: 'Personnalisation au-delà du visuel',
        bullets: [
          'Adapter les exemples, enjeux, irritants et objectifs au contexte réel du client.',
          'Nommer seulement les piliers BZ réellement pertinents pour ce mandat.',
          'Formuler les options et recommandations avec le vocabulaire du client quand il est connu.',
          'Retirer les sections génériques qui n’aident pas la décision du client.',
        ],
      },
    ],
  },
  'termes-conditions': {
    eyebrow: 'ODS BZ · Tuile 10',
    title: 'Termes et conditions',
    subtitle: 'Les clauses légales importantes doivent toujours accompagner l’ODS. Leur absence ne peut jamais être considérée comme une option par défaut.',
    source: 'Règle de gouvernance ODS BZ : utiliser la version courante et approuvée des termes et conditions BZ. La base historique pour l’infogérance est transcrite dans /knowledge/57-ods-bz-termes-conditions-infogerance.md; elle doit recevoir la validation finale autorisée avant de devenir le modèle juridique officiel.',
    icon: Scale,
    accent: 'from-slate-600 via-blue-700 to-indigo-800',
    sections: [
      {
        title: 'Règle obligatoire',
        body: 'Chaque ODS ou contrat transmis au client doit contenir les termes et conditions BZ approuvés, soit dans le document, soit dans une annexe qui en fait explicitement partie. Si le DDC ne demande aucun changement, la version standard complète s’applique et doit apparaître automatiquement.',
        bullets: [
          'Ne jamais retirer les termes et conditions pour alléger ou raccourcir une ODS.',
          'Ne jamais considérer le silence du DDC comme une demande de modification ou de suppression.',
          'Ne jamais inventer, résumer ou reformuler une clause légale : reprendre le libellé officiel approuvé.',
          'Utiliser la version courante des clauses et conserver sa date ou son numéro de version.',
        ],
      },
      {
        title: 'Profil spécifique — infogérance',
        body: 'Les conditions fournies par la direction des ventes concernent l’infogérance, le support TI et, lorsque pertinent, les services infonuagiques. Elles ne doivent pas être appliquées automatiquement à une ODS d’un autre type.',
        bullets: [
          'Reprendre les clauses standards d’infogérance lorsqu’aucune exception n’est demandée et approuvée.',
          'Inclure l’annexe de support avec les heures de service, les coordonnées, les délais de réponse estimés et le fonctionnement du service d’urgence.',
          'Inclure les règles applicables aux licences Microsoft 365, aux fournisseurs tiers, à la migration, à la résiliation et à l’exportation des données lorsqu’elles font partie du mandat.',
          'Inclure les crédits et limitations propres à l’infonuagique seulement lorsque l’ODS comprend réellement ces services.',
        ],
      },
      {
        title: 'Champs variables à compléter',
        body: 'Les éléments historiquement surlignés en jaune étaient modifiés à la main. Le bot doit maintenant les remplir à partir de l’ODS et les faire confirmer; il ne doit jamais les deviner.',
        bullets: [
          'Annexes applicables — exemple historique : A-B.',
          'Mode de paiement — prélèvement autorisé (PPA), dépôt direct ou chèque.',
          'Date de validité de la proposition — mois et année.',
          'Durée du contrat — valeur historique : 36 mois.',
          'Majoration annuelle minimale — valeur historique : 3 % à la fin de la première et de la deuxième année.',
          'Frais uniques de migration et d’implantation — règle du pouce : prévoir de 1 à 2 fois le total du service TI, selon l’ampleur de la prise en charge.',
          'Aucune formule de remboursement des frais de migration ou d’implantation ne doit apparaître par défaut. Cette clause rare exige une demande explicite du DDC et l’approbation requise.',
          'Si une valeur manque, arrêter la finalisation et demander au DDC de la confirmer.',
        ],
      },
      {
        title: 'Familles de clauses à vérifier systématiquement',
        body: 'Cette liste est un contrôle de présence, pas un modèle de rédaction juridique. Le texte exact doit venir de la source contractuelle BZ approuvée.',
        bullets: [
          'Identification des parties, autorité de signature, portée de l’ODS, ordre de priorité des documents et gestion des changements.',
          'Prix, taxes, facturation, modalités de paiement, frais applicables et conséquences d’un retard de paiement.',
          'Durée, renouvellement, suspension, résiliation et obligations qui survivent à la fin du contrat.',
          'Responsabilités du client, accès requis, dépendances, exclusions et services ou fournisseurs tiers.',
          'Confidentialité, protection des renseignements, sécurité, données, licences et propriété intellectuelle.',
          'Niveaux de service, disponibilité, crédits ou recours applicables lorsque le service le prévoit.',
          'Garanties et exclusions, limitation de responsabilité, indemnisation et force majeure.',
          'Droit applicable, règlement des différends, acceptation et signatures.',
        ],
      },
      {
        title: 'Modification demandée par un DDC',
        body: 'Un DDC peut demander une adaptation, mais la modification doit être explicite, ciblée et traçable. Le bot ne doit jamais modifier silencieusement une clause.',
        bullets: [
          'Identifier précisément la clause visée, la modification demandée et la raison d’affaires.',
          'Conserver la clause standard tant que le changement n’a pas reçu l’approbation requise chez BZ.',
          'Faire valider toute modification qui touche notamment la responsabilité, l’indemnisation, les garanties, les crédits, la résiliation, la confidentialité, les données ou la propriété intellectuelle.',
          'Documenter dans la version finale les exceptions approuvées et éviter qu’elles deviennent automatiquement le nouveau standard.',
        ],
      },
      {
        title: 'Contrôle avant émission',
        bullets: [
          'Confirmer que les termes et conditions sont présents, lisibles et rattachés clairement à l’ODS.',
          'Confirmer la version de la source contractuelle BZ utilisée.',
          'Comparer toute exception avec la version standard et confirmer son approbation.',
          'Si le texte officiel, sa version ou une approbation manque, arrêter la finalisation et demander au DDC ou au responsable autorisé de fournir ou valider l’information.',
          'Ne jamais marquer l’ODS comme prête à envoyer tant que ce contrôle n’est pas réussi.',
        ],
      },
    ],
  },
  'convention-service': {
    eyebrow: 'ODS BZ · Tuile 11',
    title: 'Convention de service 2026',
    subtitle: 'La référence contractuelle générale pour contrôler les ODS et signaler toute dérogation avant l’envoi au client.',
    source: 'Source officielle fournie par la Direction des ventes : Contrat de service 2026 V1.1. Document intégral : /documents/ods/Convention_de_service_BZ_2026_V1.1.docx. Fiche de contrôle détaillée : /knowledge/58-ods-bz-convention-service-2026-controle-ecarts.md.',
    icon: FileSignature,
    accent: 'from-rose-700 via-red-700 to-slate-900',
    sections: [
      {
        title: 'Règle obligatoire pour les bots',
        body: 'Chaque ODS doit être comparée à la Convention de service BZ 2026 V1.1. Un écart n’est pas nécessairement interdit puisque l’ODS peut avoir préséance, mais il ne doit jamais être silencieux.',
        bullets: [
          'Aviser immédiatement le DDC dans le chat lorsqu’une clause proposée diverge de la convention.',
          'Ajouter un avis visible dans l’ODS, près de la clause touchée ou dans une section « Écarts à la convention de service ».',
          'Nommer la rubrique concernée, expliquer factuellement l’écart et indiquer l’approbation requise.',
          'Conserver la clause standard tant que la dérogation n’est pas expressément approuvée.',
          'Ne pas présenter l’ODS comme prête à envoyer lorsqu’un écart bloquant demeure.',
        ],
      },
      {
        title: 'Avis à insérer dans l’ODS',
        body: 'AVIS — Écart à la convention de service BZ. La disposition ci-dessous diffère de la rubrique « [RUBRIQUE] » de la Convention de service BZ 2026 V1.1. L’écart concerne : [DESCRIPTION FACTUELLE]. Une validation explicite de [DIRECTION / RESPONSABLE AUTORISÉ / JURIDIQUE] est requise avant l’envoi au client. Tant que cette validation n’est pas obtenue, la clause standard BZ demeure applicable.',
      },
      {
        title: 'Hiérarchie contractuelle',
        bullets: [
          'L’ODS acceptée fait partie intégrante du Contrat et a préséance en cas de divergence.',
          'La préséance de l’ODS ne permet pas une dérogation silencieuse : elle doit être expresse, ciblée, justifiée, traçable et approuvée.',
          'Le Contrat général s’applique à tout sujet qui n’est pas traité ou dérogé dans l’ODS.',
          'Les conditions des fournisseurs tiers et le Formulaire d’ouverture de compte s’appliquent dans leur champ respectif.',
          'Un conflit entre une ODS négociée et une Modification ultérieure doit être soumis à une validation juridique.',
        ],
      },
      {
        title: 'Écarts bloquants à détecter',
        bullets: [
          'Service, livrable, délai, surveillance ou responsabilité non explicitement inclus et tarifé.',
          'Garantie de résultat, disponibilité continue, absence d’erreurs, restauration garantie ou cybersécurité absolue.',
          'Responsabilité BZ pour une panne Microsoft, Google, Azure ou un autre fournisseur tiers.',
          'Responsabilité illimitée, plafond supérieur ou remboursement des pertes de revenus, profits ou dommages indirects.',
          'Résiliation sans préavis ou sans frais, délai différent de 90 jours ou annulation automatique des engagements SaaS.',
          'Transfert à BZ de la propriété des données du Client ou avis de confidentialité retardé.',
          'Engagement de prix, crédit, garantie, indemnité ou ajout de Service confirmé uniquement dans le chat.',
        ],
      },
      {
        title: 'Repères contractuels importants',
        bullets: [
          'Intérêt sur les sommes impayées : 24 % par année, soit 2 % par mois.',
          'Plafond hors assurance : frais des six mois précédant l’événement pour les seuls Services concernés, hors déboursés, licences et matériel.',
          'Non-sollicitation : pendant le Contrat et 12 mois après sa fin.',
          'Défaut : préavis de 24 heures avant suspension et de 5 jours ouvrables avant résiliation pour défaut.',
          'Résiliation générale : préavis écrit de 90 jours, sommes dues et engagements tiers maintenus.',
          'Confidentialité : avis dès la prise de connaissance d’une violation ou tentative de violation.',
        ],
      },
      {
        title: 'Validation juridique obligatoire',
        body: 'Toute dérogation touchant les garanties, la responsabilité, les plafonds, les dommages, l’indemnisation, les données, la confidentialité, la propriété intellectuelle, les recours ou la résiliation doit être soumise au responsable autorisé et, lorsque pertinent, au juridique. Le bot doit signaler l’enjeu; il ne doit pas trancher lui-même.',
      },
    ],
  },
};

export default function ODSKnowledgeTileModal({ tile, isOpen, onClose }: ODSKnowledgeTileModalProps) {
  const content = tile ? tiles[tile] : null;
  if (!content) return null;

  const Icon = content.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-slate-950/75 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
          >
            <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-3xl border border-white/10 bg-slate-950 text-white shadow-2xl">
              <header className={`relative overflow-hidden bg-gradient-to-br ${content.accent} px-6 py-7 md:px-8 md:py-9`}>
                <div className="absolute -right-12 -top-16 h-44 w-44 rounded-full bg-white/15 blur-3xl" />
                <div className="relative flex items-start justify-between gap-5">
                  <div className="flex items-start gap-4">
                    <div className="rounded-2xl border border-white/25 bg-white/15 p-3 shadow-lg backdrop-blur">
                      <Icon className="h-8 w-8" />
                    </div>
                    <div>
                      <div className="text-xs font-black uppercase tracking-[0.2em] text-white/75">{content.eyebrow}</div>
                      <h2 className="mt-2 text-3xl font-black tracking-tight md:text-5xl">{content.title}</h2>
                      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/85 md:text-base">{content.subtitle}</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-full border border-white/20 bg-white/10 p-2 text-white/80 transition hover:bg-white/20 hover:text-white"
                    aria-label="Fermer"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </header>

              <main className="space-y-6 p-6 md:p-8">
                <div className="rounded-2xl border border-sky-300/15 bg-sky-300/10 p-5 text-sm leading-relaxed text-sky-50">
                  <div className="mb-2 flex items-center gap-2 font-bold text-sky-200">
                    <Sparkles className="h-4 w-4" />
                    Source de conception
                  </div>
                  {content.source}
                </div>

                <div className="grid gap-5">
                  {content.sections.map((section, index) => (
                    <section key={section.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 md:p-6">
                      <div className="mb-3 flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-sm font-black text-cyan-200">{index + 1}</span>
                        <h3 className="text-xl font-black">{section.title}</h3>
                      </div>
                      {section.body && <p className="text-sm leading-relaxed text-slate-300 md:text-base">{section.body}</p>}
                      {section.bullets && (
                        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-300 md:text-base">
                          {section.bullets.map((bullet) => (
                            <li key={bullet} className="flex gap-3">
                              <CheckCircle className="mt-0.5 h-4 w-4 flex-none text-emerald-300" />
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {section.imageUrl && (
                        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 md:p-6">
                          <img
                            src={section.imageUrl}
                            alt={section.imageAlt ?? ''}
                            className="mx-auto max-h-56 w-full object-contain"
                          />
                        </div>
                      )}
                    </section>
                  ))}
                </div>

              </main>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
